import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import { useTryAsync } from "no-try";
import { useRouter } from "next/router";
import cotributeDefaults from "@dreambigger/shared/src/assets/cotribute-defaults";
import {
  useUser,
  useMixpanel,
  useGA,
  useSegment,
} from "@dreambigger/shared/src/hooks";
import InspireAnalytics, {
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import HeaderTextCard from "@dreambigger/communities/src/ui/header-text-card";
import { CloudinaryUpload } from "@dreambigger/design-system/src/components/";

import {
  Event as EventType,
  Community,
  OrgBootstrap,
} from "@dreambigger/shared/src/types";
import {
  Divider,
  Spin,
  Group,
  Banner,
  Input,
  Form,
  InputNumber,
  Checkbox,
  Button,
  DecoratedImage,
} from "@dreambigger/design-system/src/components/";
import {
  api,
  tokenHelper,
  communityApi,
  orgApi,
  eventApi,
  storyApi,
} from "@dreambigger/shared/src/api/weserve/api-helper";
import { StoryParams } from "@dreambigger/shared/src/api/stories";
import Sharebar from "@dreambigger/design-system/src/components/molecules/sharebar";
import {
  CardDetails,
  CardFundraisingGoal,
} from "@dreambigger/shared/src/components/board/feed-cards/card-components";
import { zipCodeValidationRule } from "../../util/validation";

export type EventProps = {
  event: EventType;
  orgBootstrap: OrgBootstrap;
  community: Community;
};

// Get static paths
export const getEventStaticPaths: GetStaticPaths = async () => {
  const res = await api.get("/active-event-ids");
  const eventIds: number[] = res.data.eventIds;

  // Only statically generate the last X amount of events. Default to 300
  const staticPageLimit = process.env.ENTITY_STATIC_PAGE_LIMIT || 300;

  return {
    paths: eventIds
      .slice(0, Math.min(Number(staticPageLimit), eventIds.length))
      .map((id) => ({ params: { eventId: String(id) } })),
    fallback: true,
  };
};

// Get static props to be passed into Event component.
export const getEventShareStaticProps: GetStaticProps = async ({ params }) => {
  const eventId = params!.eventId as string;
  // *** Get event data.
  const [eventError, eventRes] = await useTryAsync(() =>
    eventApi.getById(Number(eventId))
  );
  if (eventError) {
    console.error("Unable to load event:", eventError);
    return {
      notFound: true,
    };
  }

  // Assign results to variables.
  const event: EventType = eventRes!.data;

  // Get orgBootstrap data.
  const orgBootstrapRes = await orgApi.getOrgBootstrap(event.organization.id);
  const orgBootstrap = orgBootstrapRes.data;
  // Return all constants as props.
  const returnProps = {
    event,
    orgBootstrap,
  } as EventProps;

  // Get community data
  const [communityError, communityResponse] = await useTryAsync(() =>
    communityApi.getBySlug(orgBootstrap?.slug)
  );
  // Add community data to returnProps
  if (!communityError && communityResponse?.data) {
    returnProps.community = communityResponse.data;
  }
  // Return the returnProps, revalidating every 120 seconds.
  return {
    props: returnProps,
    revalidate: 120,
  };
};

const EventsSharePage = (props: EventProps) => {
  const [userLoading, { data: me }] = useUser(api, tokenHelper);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [storyId, setStoryId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const analyticsInitialized = useRef(false);
  const { TextArea } = Input;
  const router = useRouter();

  // Load analytics hooks
  const googleAnalytics = useGA();
  const mixpanel = useMixpanel();
  const segment = useSegment();

  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Track page view for analytics
  useEffect(() => {
    if (userLoading || analyticsInitialized.current) return;
    analyticsInitialized.current = true;

    setTimeout(() => {
      googleAnalytics.setDefaultDimensions(props.community?.slug, me);
      googleAnalytics.pageview(window.location.pathname);
      InspireAnalytics.track({
        eventName: InspireAnalyticEvents.PAGE_VIEW,
        properties: {
          Page: window.location.pathname,
          "Page Type": "Event Story Share Page",
          "Host Name": window.location.host,
          "Event ID": props.event.id,
          "Event Name": props.event.name,
          "Community Slug": props.community?.slug,
        },
        mixpanel,
        segment,
      });
    }, 500);
  }, [userLoading]);

  useEffect(() => {
    const CLOUDINARY_SCRIPT_SRC =
      "https://widget.cloudinary.com/v2.0/global/all.js";

    // Check if script already exists and cloudinary is available
    const existingScript = document.querySelector(
      `script[src="${CLOUDINARY_SCRIPT_SRC}"]`
    );

    if (existingScript && (window as any).cloudinary) {
      // Script already loaded and cloudinary is ready
      setScriptLoaded(true);
      return;
    }

    // If script exists but cloudinary isn't ready yet, wait for it with timeout
    if (existingScript) {
      let attempts = 0;
      const MAX_ATTEMPTS = 100; // 10 seconds max (100 * 100ms)
      const checkCloudinary = setInterval(() => {
        attempts++;
        if ((window as any).cloudinary) {
          clearInterval(checkCloudinary);
          setScriptLoaded(true);
        } else if (attempts >= MAX_ATTEMPTS) {
          clearInterval(checkCloudinary);
          console.error("Cloudinary script failed to load after timeout");
          setScriptLoaded(true); // Allow page to render anyway
        }
      }, 100);
      return () => clearInterval(checkCloudinary);
    }

    const script = document.createElement("script");
    script.src = CLOUDINARY_SCRIPT_SRC;
    script.type = "text/javascript";

    script.onload = () => {
      setScriptLoaded(true);
    };

    // Append the script to the document body
    document.body.appendChild(script);

    // Note: We don't remove the script on unmount because it may be needed
    // by other components, and removing it could cause issues
  }, []);

  useEffect(() => {
    let timeoutId: any;

    if (error) {
      // If error is true, set a timeout to clear the error state after 5 seconds
      timeoutId = setTimeout(() => {
        setError(false);
      }, 5000); // 5000 milliseconds = 5 seconds
    }

    // Clear the timeout when the component unmounts or when error becomes false
    return () => {
      clearTimeout(timeoutId);
    };
  }, [error]);

  const eventUrl = `/events/${props.event.id}/detail`;

  const handleEventClick = () => {
    window.location.pathname = window.location.pathname.replace(
      "share",
      "detail"
    );
  };

  const createStoryPayload = (values: any) => {
    const commonPayload = {
      description: values?.description || " ",
      visibilityStatus: 0,
      event: {
        id: props.event.id,
      },
    };

    const guestPayload = {
      firstName: values?.firstName,
      lastName: values?.lastName,
      email: values?.email,
      zip: values?.zipCode,
    };

    let storyPayload: StoryParams = me
      ? { ...commonPayload }
      : { ...commonPayload, guest: { ...guestPayload } };

    // Add volunteer hours if time tracking is enabled
    if (
      props.community?.organization?.meta?.storyTimeTrack &&
      values.volunteerHours
    ) {
      storyPayload = { ...storyPayload, minutes: values.volunteerHours * 60 };
    }

    // Add image data if an image was uploaded
    if (values.image) {
      storyPayload = {
        ...storyPayload,
        images: [
          {
            sort: 0,
            status: "active",
            imageId: values.image.public_id,
            imageVersion: `v${values.image.version}`,
            meta: {
              src: values.image.secure_url,
              height: values.image.height,
              width: values.image.width,
              size: values.image.bytes,
              format: values.image.format,
            },
          },
        ],
      };
    }

    return storyPayload;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setSubmitting(true);
      const storyPayload = createStoryPayload(values);

      // logic for sending story to api
      await storyApi
        .createStory(storyPayload)
        .then((res) => {
          // Store story ID for social sharing
          if (res?.data?.id) {
            setStoryId(res.data.id);
          }
          setSuccess(true);

          // Track successful story share
          googleAnalytics.event({
            category: "Story Share",
            action: "Story Submitted",
            label: `Event: ${props.event.id}`,
          });
          InspireAnalytics.track({
            eventName: InspireAnalyticEvents.STORY_SHARED,
            properties: {
              "Event ID": props.event.id,
              "Event Name": props.event.name,
              "Community Slug": props.community?.slug,
              "Story ID": res?.data?.id,
              "Has Image": !!values.image,
              "Is Guest": !me,
            },
            mixpanel,
            segment,
          });

          // People profile aggregates. Property names mirror legacy
          // volunteerpostctrl.js exactly: a `Shared Volunteer Experience` flag
          // (not a timestamp) plus the `zzBMVP1: Total Volunteer Experiences
          // Shared` counter so existing Mixpanel reports keep updating.
          InspireAnalytics.setPeopleProperties({
            mixpanel,
            properties: {
              "Shared Volunteer Experience": "Yes",
            },
          });
          InspireAnalytics.incrementPeopleProperty({
            mixpanel,
            property: "zzBMVP1: Total Volunteer Experiences Shared",
          });
        })
        .catch((err) => {
          setError(true);
        })
        .finally(() => {
          setSubmitting(false);
        });
    } catch (error) {
      console.error("Error occurred:", error);
      setSubmitting(false);
    }
  };

  const EventsPhoto = (
    <div
      className="pb-4 mb-1 flex bg-white flex-column"
      style={{
        top: "17%",
        border: "1px solid #e8e8e8",
        borderRadius: "9px",
        boxShadow: "0 2px 4px -1px #e8e8e8",
        transition: "top 0.3s ease",
        maxWidth: "600px",
      }}
    >
      <DecoratedImage
        src={props.event?.images[0]?.meta?.src || cotributeDefaults.coverImage}
        alt="Event Img"
        cornerButtonEnabled={true}
        cornerButtonIcon="images"
        bannerText="Happening Now!"
        style={{
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: "rgb(0 0 0 / 16%) 0px 1px 4px",
        }}
        className="z-99"
      />
      <div className="ph-5 pv-4">
        <CardDetails
          allDay={props.event.allDay}
          title={props.event.name || ""}
          host={props.event.host || ""}
          location={props.event.location.name || ""}
          startAt={props.event.startAt || ""}
          endAt={props.event.endAt || ""}
          font={"proxima-nova"}
          timeZone={props.event.timeZone}
        />
      </div>
      {props.event.campaign && (
        <CardFundraisingGoal
          community={props.community}
          campaign={props.event.campaign}
        />
      )}
    </div>
  );

  const ImageGuidelines = (
    <HeaderTextCard
      header="IMAGE GUIDELINES"
      text="Please share inspiring and appropriate images that will encourage others. Any photo that is offensive to a reasonable person will be rejected."
    />
  );
  function renderShareFormCard() {
    if (userLoading) {
      return <Spin style={{ width: "100%", marginTop: 150 }} />;
    }
    return (
      <div className="br-3 bg-white ">
        <div
          className="w-100 bg-primary f-8 tc white pv-4"
          style={{ borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}
        >
          Share a Photo or Comment
        </div>
        <Form
          onFinish={handleSubmit}
          form={form}
          className="w-75 pa-4"
          style={{ margin: "0 auto" }}
        >
          <CloudinaryUpload
            form={form}
            fieldName="image"
            cloudName="cotribute"
            uploadPreset="wzxvdqg5"
            returnImageDetails={true}
            onUpload={() => {
              form.validateFields(["description"]);
              googleAnalytics.event({
                category: "Event Create/Edit",
                action: "Cloudinary Upload Widget - Upload successful",
                label: "Upload Complete",
              });
            }}
            onSelect={() => {
              googleAnalytics.event({
                category: "Event Create/Edit",
                action: "Cloudinary Upload Widget - Image selection",
                label: "Select Image",
              });
            }}
            onError={() => {
              googleAnalytics.event({
                category: "Event Create/Edit",
                action: "Cloudinary Upload Widget - Image Upload",
                label: "Upload Error",
              });
            }}
            onAbort={() => {
              googleAnalytics.event({
                category: "Event Create/Edit",
                action: "Cloudinary Upload Widget - No Image was uploaded",
                label: "Upload Abort",
              });
            }}
          />

          <div className="fwb f-4">
            {props.community?.meta?.marketing?.shareCta ||
              "Share your story and inspire others!"}
          </div>
          <Form.Item
            name="description"
            rules={[
              {
                validator: (_, value) => {
                  const imageValue = form.getFieldValue("image");
                  if (!value?.trim() && !imageValue) {
                    return Promise.reject(
                      "Please upload a photo or enter a comment"
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="I love supporting those in need!"
              className="w-100"
              maxLength={6000}
            />
          </Form.Item>
          {!me && (
            <>
              <div className="fwb f-4">First Name</div>
              <Form.Item
                name="firstName"
                rules={[
                  {
                    required: true,
                    message: "Please enter your first name",
                  },
                ]}
              >
                <Input placeholder="Matt" />
              </Form.Item>
              <div className="fwb f-4">Last Name</div>
              <Form.Item
                name="lastName"
                rules={[
                  {
                    required: true,
                    message: "Please enter your last name",
                  },
                ]}
              >
                <Input placeholder="Smith" />
              </Form.Item>
              <div className="fwb f-4">Email</div>
              <Form.Item
                name="email"
                rules={[
                  {
                    type: "email",
                    message: "Please enter a valid email address!",
                  },
                  {
                    required: true,
                    message: "Please enter your email address!",
                  },
                ]}
              >
                <Input placeholder="your@email.com" />
              </Form.Item>
            </>
          )}

          {me && props.community?.organization?.meta?.storyTimeTrack && (
            <>
              {/* convert to minutes for backend */}
              <div className="fwb f-4">Volunteer Time Spent (Hours)</div>
              <Form.Item name="volunteerHours">
                <InputNumber precision={0} min={0} />
              </Form.Item>
            </>
          )}
          {!me && (
            <>
              <div className="fwb f-4">Zip Code</div>
              <Form.Item
                name="zipCode"
                rules={[
                  {
                    required: true,
                    message: "Please enter your zip code.",
                    validator(_, value) {
                      if (value && value.length === 5 && !isNaN(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Please enter a valid zip code.")
                      );
                    },
                  },
                ]}
              >
                <Input maxLength={5} placeholder="90236" />
              </Form.Item>{" "}
            </>
          )}
          <Form.Item
            name="terms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          "Please agree to Content Submission Agreement."
                        )
                      ),
              },
            ]}
          >
            <Checkbox className="f-4">
              {" "}
              By submitting a photo or comment, I am agreeing to the{" "}
              <a
                href={
                  props.community?.meta?.marketing?.contentSubmission ||
                  cotributeDefaults.contentSubmissionAgreement
                }
                style={{ color: "#1890ff", textDecoration: "underline" }}
              >
                Content Submission Agreement.
              </a>
            </Checkbox>
          </Form.Item>
          <Divider />
          <div className="flex justify-center pv-6">
            <Button
              className="ant-btn-primary primary bg-gray-4 darkenPrimaryBkd mr-3 "
              style={{ width: "45%" }}
              onClick={handleEventClick}
            >
              CANCEL
            </Button>
            <Button
              style={{ width: "45%" }}
              className="ant-btn-primary white ml-3"
              htmlType="submit"
              loading={submitting}
            >
              SHARE NOW
            </Button>
          </div>
        </Form>
      </div>
    );
  }

  function getStoryShareUrl(): string {
    if (!storyId) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const baseUrl = `${origin}/stories/${storyId}`;
    return me?.id ? `${baseUrl}?atr=${me.id}` : baseUrl;
  }

  const storyShareUrl = getStoryShareUrl();

  const handleShareClick = useCallback(
    (shareType: string) => {
      eventApi.trackSocialShare(props.event.id, shareType);
    },
    [props.event.id]
  );

  const shareAnalyticsProperties = useMemo(
    () => ({
      "Event Type": props.event.isFundraiser ? "fundraiser" : "event",
      "Event ID": props.event.id,
      "Event Name": props.event.name,
      groupId: props.event.organization.id,
      "Group Name": props.event.organization.name,
    }),
    [
      props.event.id,
      props.event.name,
      props.event.isFundraiser,
      props.event.organization.id,
      props.event.organization.name,
    ]
  );

  const SuccessCard = (
    <div className="br-3 bg-white ">
      <div
        className="w-100 bg-primary f-8 tc white pv-4"
        style={{ borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}
      >
        Your Story Has Been Posted!
      </div>
      <div className="f-6 fwb tc pv-3">Good things are meant to be shared</div>
      <Sharebar
        labeledButtons
        shareUrl={storyShareUrl}
        email={false}
        clipboard={false}
        onShare={handleShareClick}
        analyticsProperties={shareAnalyticsProperties}
      />
      <div className="f-4 tc pv-6">
        Inspire your friends and family by sharing your post on social media.
      </div>
      <Divider />
      <div className="flex justify-center">
        <Button
          className="ant-btn-primary white f-2 mv-4 mr-6 ml-6"
          onClick={handleEventClick}
        >
          BACK TO EVENT
        </Button>
      </div>
    </div>
  );

  if (!scriptLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <div className="default-wrapper-padding pv-6">
      {error && (
        <Banner
          text1="Your story did not meet our community guidelines. Please adjust and try again. You may also reach out to customer support if you need help."
          closable={true}
          textColor="white"
          className=""
          style={{
            backgroundColor: "#bc362e",
            position: "fixed",
            zIndex: "9999",
          }}
        />
      )}

      <Group md="horizontal" spacing={5} style={{ minHeight: "600px" }}>
        <Group className="md_w-66">
          {success ? SuccessCard : renderShareFormCard()}
        </Group>
        <Group className="md_w-33 md-pl-5">
          {EventsPhoto}
          {ImageGuidelines}
        </Group>
      </Group>
    </div>
  );
};

export default EventsSharePage;
